import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Heart } from 'lucide-react';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import axios from 'axios';

export const AuthPage = () => {
  const [mode, setMode] = useState('login'); // 'login', 'register', or 'forgot'
  const [step, setStep] = useState('phone'); // For register/forgot: 'phone', 'otp', 'password'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { setToken, setUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Countdown timer for resend OTP
  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/send-otp`, {
        phone_number: phoneNumber
      });

      if (response.data.success) {
        toast.success('OTP sent to your phone!');
        setStep('otp');
        setCountdown(60); // 60 seconds cooldown
        
        // For development - show OTP in console
        if (response.data.otp_code) {
          console.log('OTP Code:', response.data.otp_code);
          toast.info(`Development: OTP is ${response.data.otp_code}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-otp`, {
        phone_number: phoneNumber,
        otp_code: otp
      });

      if (response.data.success) {
        toast.success('Phone verified! Please set your password.');
        setStep('password');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegistration = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        phone_number: phoneNumber,
        otp_code: otp,
        password: password
      });

      if (response.data.success) {
        setToken(response.data.token);
        setUser(response.data.user);
        localStorage.setItem('token', response.data.token);
        toast.success('Welcome to Meet Ethiopians! Let\'s set up your profile.');
        navigate('/setup');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
        phone_number: phoneNumber,
        password: password
      });

      if (response.data.success) {
        setToken(response.data.token);
        setUser(response.data.user);
        localStorage.setItem('token', response.data.token);
        toast.success('Welcome back!');
        navigate('/discover');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-otp`, {
        phone_number: phoneNumber
      });

      if (response.data.success) {
        toast.success('New OTP sent!');
        setCountdown(60);
        setOtp('');
        
        // For development
        if (response.data.otp_code) {
          console.log('New OTP Code:', response.data.otp_code);
          toast.info(`Development: OTP is ${response.data.otp_code}`);
        }
      }
    } catch (error) {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStep('phone');
    setOtp('');
  };

  // Forgot Password Handlers
  const handleForgotPasswordSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        phone_number: phoneNumber
      });

      if (response.data.success) {
        toast.success('Reset code sent to your phone!');
        setStep('otp');
        setCountdown(60);
        
        // For development - show OTP in console
        if (response.data.otp_code) {
          console.log('Reset OTP Code:', response.data.otp_code);
          toast.info(`Development: Reset code is ${response.data.otp_code}`);
        }
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password`, {
        phone_number: phoneNumber,
        otp_code: otp,
        new_password: password
      });

      if (response.data.success) {
        toast.success('Password reset successful! Please login.');
        setMode('login');
        setStep('phone');
        setOtp('');
        setPassword('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleResendForgotOTP = async () => {
    if (countdown > 0) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, {
        phone_number: phoneNumber
      });

      if (response.data.success) {
        toast.success('New reset code sent!');
        setCountdown(60);
        setOtp('');
        
        if (response.data.otp_code) {
          console.log('New Reset OTP Code:', response.data.otp_code);
          toast.info(`Development: Reset code is ${response.data.otp_code}`);
        }
      }
    } catch (error) {
      toast.error('Failed to resend reset code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-pink-50 flex items-center justify-center p-6" data-testid="auth-page">
      <div className="w-full max-w-md space-y-8">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher variant="outline" />
        </div>
        
        <div className="text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-20 h-20 rounded-full bg-gradient-to-br from-electric-coral to-deep-violet flex items-center justify-center">
              <Heart className="w-10 h-10 text-white" fill="white" data-testid="logo-icon" />
            </div>
          </div>
          <h1 className="font-heading text-5xl tracking-tight text-slate-900" data-testid="app-title">{t('appName')}</h1>
          <p className="text-lg text-slate-600">{t('tagline')}</p>
        </div>

        <Card className="p-8 rounded-3xl shadow-lg border-0 bg-white/80 backdrop-blur-md">
          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-2xl font-semibold text-slate-900">{t('signIn')}</h2>
                <p className="text-sm text-slate-600 mt-1">Welcome back to Meet Ethiopians</p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">Phone Number</label>
                <Input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="0912345678 or +251912345678"
                  className="rounded-xl border-slate-200"
                  required
                  data-testid="login-phone-input"
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">{t('password')}</label>
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="rounded-xl border-slate-200"
                  required
                  data-testid="login-password-input"
                />
              </div>

              <div className="text-right">
                <button
                  type="button"
                  onClick={() => { setMode('forgot'); setStep('phone'); setPassword(''); setOtp(''); }}
                  className="text-sm text-electric-coral hover:text-deep-violet transition-colors"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>

              <Button
                type="submit"
                className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300 shadow-lg h-12 text-base font-semibold"
                disabled={loading}
                data-testid="login-button"
              >
                {loading ? 'Signing in...' : t('signIn')}
              </Button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => { setMode('register'); setStep('phone'); setPassword(''); }}
                  className="text-sm text-slate-600 hover:text-electric-coral transition-colors"
                  data-testid="switch-to-register"
                >
                  {t('dontHaveAccount')}
                </button>
              </div>
            </form>
          ) : mode === 'register' ? (
            <>
              {step === 'phone' && (
                <form onSubmit={handleSendOTP} className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-semibold text-slate-900">Create Account</h2>
                    <p className="text-sm text-slate-600 mt-1">We'll verify your phone number</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Phone Number</label>
                    <Input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="0912345678 or +251912345678"
                      className="rounded-xl border-slate-200 text-lg"
                      required
                      data-testid="register-phone-input"
                    />
                    <p className="text-xs text-slate-500">Ethiopian phone numbers only</p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300 shadow-lg h-12 text-base font-semibold"
                    disabled={loading || !phoneNumber}
                    data-testid="send-otp-button"
                  >
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setPhoneNumber(''); setOtp(''); }}
                      className="text-sm text-slate-600 hover:text-electric-coral transition-colors"
                      data-testid="switch-to-login"
                    >
                      {t('alreadyHaveAccount')}
                    </button>
                  </div>
                </form>
              )}

              {step === 'otp' && (
                <form onSubmit={handleVerifyOTP} className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-semibold text-slate-900">Verify Phone Number</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Code sent to {phoneNumber}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">6-Digit Code</label>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      className="rounded-xl border-slate-200 text-center text-2xl tracking-widest font-semibold"
                      maxLength={6}
                      required
                      data-testid="otp-input"
                      autoFocus
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300 shadow-lg h-12 text-base font-semibold"
                    disabled={loading || otp.length !== 6}
                    data-testid="verify-otp-button"
                  >
                    {loading ? 'Verifying...' : 'Verify Phone'}
                  </Button>

                  <div className="flex justify-between items-center">
                    <button
                      type="button"
                      onClick={handleBack}
                      className="text-sm text-slate-600 hover:text-electric-coral transition-colors"
                      data-testid="back-button"
                    >
                      ← Change Number
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      disabled={countdown > 0}
                      className={`text-sm font-medium transition-colors ${
                        countdown > 0 
                          ? 'text-slate-400 cursor-not-allowed' 
                          : 'text-electric-coral hover:text-deep-violet'
                      }`}
                      data-testid="resend-otp-button"
                    >
                      {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                    </button>
                  </div>
                </form>
              )}

              {step === 'password' && (
                <form onSubmit={handleCompleteRegistration} className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-semibold text-slate-900">Create Password</h2>
                    <p className="text-sm text-slate-600 mt-1">Choose a strong password</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">{t('password')}</label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      className="rounded-xl border-slate-200"
                      minLength={8}
                      required
                      data-testid="register-password-input"
                      autoFocus
                    />
                    <p className="text-xs text-slate-500">At least 8 characters</p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300 shadow-lg h-12 text-base font-semibold"
                    disabled={loading || password.length < 8}
                    data-testid="complete-registration-button"
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              )}
            </>
          ) : (
            /* Forgot Password Mode */
            <>
              {step === 'phone' && (
                <form onSubmit={handleForgotPasswordSendOTP} className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-semibold text-slate-900">Reset Password</h2>
                    <p className="text-sm text-slate-600 mt-1">Enter your phone number to receive a reset code</p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">Phone Number</label>
                    <Input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="0912345678 or +251912345678"
                      className="rounded-xl border-slate-200 text-lg"
                      required
                      data-testid="forgot-phone-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300 shadow-lg h-12 text-base font-semibold"
                    disabled={loading || !phoneNumber}
                    data-testid="forgot-send-otp-button"
                  >
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </Button>

                  <div className="text-center">
                    <button
                      type="button"
                      onClick={() => { setMode('login'); setPhoneNumber(''); }}
                      className="text-sm text-slate-600 hover:text-electric-coral transition-colors"
                      data-testid="back-to-login"
                    >
                      ← Back to Login
                    </button>
                  </div>
                </form>
              )}

              {step === 'otp' && (
                <form onSubmit={handleResetPassword} className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-semibold text-slate-900">Reset Your Password</h2>
                    <p className="text-sm text-slate-600 mt-1">
                      Enter the code sent to {phoneNumber}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">6-Digit Code</label>
                    <Input
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="123456"
                      className="rounded-xl border-slate-200 text-center text-2xl tracking-widest font-semibold"
                      maxLength={6}
                      required
                      data-testid="forgot-otp-input"
                      autoFocus
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-700">New Password</label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Minimum 8 characters"
                      className="rounded-xl border-slate-200"
                      minLength={8}
                      required
                      data-testid="new-password-input"
                    />
                    <p className="text-xs text-slate-500">At least 8 characters</p>
                  </div>

                  <Button
                    type="submit"
                    className="w-full rounded-full bg-gradient-to-r from-electric-coral to-deep-violet hover:scale-105 transition-all duration-300 shadow-lg h-12 text-base font-semibold"
                    disabled={loading || otp.length !== 6 || password.length < 8}
                    data-testid="reset-password-button"
                  >
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>

                  <div className="flex justify-between items-center">
                    <button
                      type="button"
                      onClick={() => { setStep('phone'); setOtp(''); setPassword(''); }}
                      className="text-sm text-slate-600 hover:text-electric-coral transition-colors"
                    >
                      ← Change Number
                    </button>
                    
                    <button
                      type="button"
                      onClick={handleResendForgotOTP}
                      disabled={countdown > 0}
                      className={`text-sm font-medium transition-colors ${
                        countdown > 0 
                          ? 'text-slate-400 cursor-not-allowed' 
                          : 'text-electric-coral hover:text-deep-violet'
                      }`}
                    >
                      {countdown > 0 ? `Resend in ${countdown}s` : 'Resend Code'}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </Card>
      </div>
    </div>
  );
};